import type { PointerEvent, ReactNode, RefObject } from 'react';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { RippleState } from '@/hooks/useAuthForm';

interface HeaderAction {
  label: string;
  onClick: () => void;
  testId?: string;
}

interface PrimaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  testId: string;
  buttonRef?: RefObject<HTMLButtonElement>;
  ripple?: RippleState;
  onRippleEnd?: () => void;
  onPointerDown?: (event: PointerEvent<HTMLButtonElement>) => void;
}

interface AuthFormLayoutProps {
  testId: string;
  screenId?: string;
  onBack?: () => void;
  backTestId?: string;
  errorTestId?: string;
  rightAction?: HeaderAction;
  headerContent?: ReactNode;
  children: ReactNode;
  error?: string | ReactNode;
  primaryAction: PrimaryAction;
  afterAction?: ReactNode;
  footer?: ReactNode;
  contentClassName?: string;
}

export function AuthFormLayout({
  testId,
  screenId,
  onBack,
  backTestId,
  errorTestId,
  rightAction,
  headerContent,
  children,
  error,
  primaryAction,
  afterAction,
  footer,
  contentClassName,
}: AuthFormLayoutProps) {
  return (
    <div
      data-testid={testId}
      data-screen-id={screenId}
      className="min-h-screen min-h-[100dvh] flex flex-col justify-center bg-bg-accent-pink-soft"
    >
      <div
        className="w-full flex flex-col min-h-screen min-h-[100dvh] overflow-y-auto"
        style={{
          maxWidth: '375px',
          margin: '0 auto',
        }}
      >
        <div
          className="flex items-center px-4 py-4 bg-bg-primary sticky top-0 z-10"
          style={{ minHeight: '56px' }}
        >
          {onBack && (
            <button
              data-testid={backTestId}
              onClick={onBack}
              className="w-10 h-10 flex items-center justify-center -ml-2"
              aria-label="Назад"
            >
              <ChevronLeft className="w-6 h-6 text-text-primary" />
            </button>
          )}

          {rightAction && (
            <button
              data-testid={rightAction.testId}
              onClick={rightAction.onClick}
              className="ml-auto text-text-primary"
              style={{
                fontFamily: 'CoFo Gothic, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
              }}
            >
              {rightAction.label}
            </button>
          )}
        </div>

        <div className="flex-1 bg-bg-primary px-4 pb-8">
          {headerContent && (
            <div className="mt-4 mb-6">
              {headerContent}
            </div>
          )}

          <div className={cn('space-y-6', contentClassName)}>
            {children}

            {error && (
              <div
                data-testid={errorTestId || `${testId}-error`}
                className="bg-bg-accent-pink-soft border-2 border-accent-pink"
                style={{
                  borderRadius: '16px',
                  padding: '12px 16px',
                }}
              >
                {typeof error === 'string' ? (
                  <p
                    className="text-text-accent-pink"
                    style={{
                      fontFamily: 'CoFo Gothic, sans-serif',
                      fontSize: '14px',
                    }}
                  >
                    {error}
                  </p>
                ) : (
                  error
                )}
              </div>
            )}

            <button
              ref={primaryAction.buttonRef}
              data-testid={primaryAction.testId}
              onClick={primaryAction.onClick}
              onPointerDown={primaryAction.onPointerDown}
              disabled={primaryAction.disabled}
              className={cn(
                'relative w-full overflow-hidden transition-colors',
                primaryAction.disabled
                  ? 'bg-bg-button-secondary text-text-tertiary'
                  : 'bg-bg-button-primary text-text-secondary'
              )}
              style={{
                height: '52px',
                borderRadius: '20px',
                fontFamily: 'CoFo Gothic, sans-serif',
                fontWeight: 500,
                fontSize: '16px',
              }}
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                {primaryAction.loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  primaryAction.label
                )}
              </span>

              {primaryAction.ripple?.active && (
                <span
                  className="absolute bg-text-secondary/30 rounded-full pointer-events-none"
                  style={{
                    left: primaryAction.ripple.x,
                    top: primaryAction.ripple.y,
                    transform: 'translate(-50%, -50%)',
                    animation: 'ripple-auth 0.6s ease-out forwards',
                  }}
                  onAnimationEnd={primaryAction.onRippleEnd}
                />
              )}
            </button>

            {afterAction}
          </div>
        </div>

        {footer && (
          <div
            className="px-4 py-6 flex items-center justify-center bg-bg-primary"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 24px)' }}
          >
            {footer}
          </div>
        )}
      </div>

      <style>{`
        @keyframes ripple-auth {
          0% { width: 0; height: 0; opacity: 0.5; }
          100% { width: 500px; height: 500px; opacity: 0; }
        }
      `}</style>
    </div>
  );
}
